import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import GenderSelector from "../../applicationUi/components/GenderSelector";
import PronounSelector from "../../applicationUi/components/PronounSelector";
import GoogleMapsAutocomplete from "../../applicationUi/components/GoogleAutoComplete";
import { useDispatch, useSelector } from "react-redux";
import { updateClient, fetchClientById } from "../../store/thunks/clientsThunk";
import { logError, logInfo } from "../../utils/logger";
import { toast } from "react-toastify";
import { TrashIcon, PlusIcon, PencilIcon } from "@heroicons/react/24/outline";
import CircularButton from "../../applicationUi/components/CircularButton";
import ModifyClinicianModal from "../../modals/ModifyClinicianModal";
import StatusHistoryModal from "../../modals/StatusHistoryModal";

export default function ClientDetails() {
  const dispatch = useDispatch();
  const [treatingClinician, setTreatingClinician] = useState(null);
  const [supervisingClinician, setSupervisingClinician] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'saving', 'saved', 'error'
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  
  // New state for unified clinician modal
  const [showClinicianModal, setShowClinicianModal] = useState(false);
  const [clinicianModalType, setClinicianModalType] = useState(null); // "treating" or "supervising"
  
  // Status history modal state
  const [showStatusHistoryModal, setShowStatusHistoryModal] = useState(false);
  
  const { selectedTreatingClinician, selectedSupervisingClinician } =
    useSelector((state) => state.clinicians);
  const client = useSelector((state) => state.clients.selectedClient);

  console.log("client", client);

  const [formData, setFormData] = useState({
    clientId: "",
    firstName: "",
    lastName: "",
    dateOfBirth: null,
    email: "",
    additionalEmails: [{ label: "", email: "" }],
    healthCardNumber: "",
    gender: "",
    pronouns: "",
    QBOClientId: "",
    address: {
      aptUnit: "",
      streetNumber: "",
      streetName: "",
      city: "",
      province: "",
      postalCode: "",
      country: "Canada",
    },
    phoneNumbers: [
      { type: "", number: "", note: "", isMobile: false, smsConsent: false },
    ],
    treatingClinician: { current: null, history: [] },
    supervisingClinician: { current: null, history: [] },
    clientStatus: "",
    statusChange: {
      recallDate: null,
      comment: "",
    },
  });

  // For resetting the form when Cancel is clicked
  const [originalData, setOriginalData] = useState({ ...client });

  useEffect(() => {
    if (client) {
      // Helper function to create Date object without timezone offset
      const parseDateWithoutTimezone = (dateString) => {
        if (!dateString) return null;
        console.log(`[Client Details] Original DOB from DB:`, dateString);
        // Extract just the date part (YYYY-MM-DD) from ISO string
        const datePart = dateString.split('T')[0];
        const [year, month, day] = datePart.split('-');
        console.log(`[Client Details] Parsed date parts - Year: ${year}, Month: ${month}, Day: ${day}`);
        // Create date using local timezone at midnight to avoid offset issues
        const parsedDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        console.log(`[Client Details] Created Date object:`, parsedDate);
        console.log(`[Client Details] Date will display as:`, parsedDate.toLocaleDateString());
        return parsedDate;
      };

      setFormData({
        clientId: client.clientId ?? "",
        firstName: client.firstName ?? "",
        lastName: client.lastName ?? "",
        dateOfBirth: client.dateOfBirth
          ? parseDateWithoutTimezone(client.dateOfBirth)
          : null,
        email: client.email ?? "",
        additionalEmails: client.additionalEmails ?? [{ label: "", email: "" }],
        healthCardNumber: client.healthCardNumber ?? "",
        gender: client.gender ?? "",
        pronouns: client.pronouns ?? "",
        QBOClientId: client.QBOClientId ?? "",
        address: {
          aptUnit: client.address?.aptUnit ?? "",
          streetNumber: client.address?.streetNumber ?? "",
          streetName: client.address?.streetName ?? "",
          city: client.address?.city ?? "",
          province: client.address?.province ?? "",
          postalCode: client.address?.postalCode ?? "",
          country: client.address?.country ?? "",
        },
        phoneNumbers: client.phoneNumbers?.map((phone) => ({
          type: phone.type ?? "",
          number: phone.number ?? "",
          note: phone.note ?? "",
          isMobile: phone.isMobile ?? false,
          smsConsent: phone.smsConsent ?? false,
        })) ?? [
          {
            type: "",
            number: "",
            note: "",
            isMobile: false,
            smsConsent: false,
          },
        ],
        treatingClinician: client.treatingClinician ?? { current: null, history: [] },
        supervisingClinician: client.supervisingClinician ?? { current: null, history: [] },
        clientStatus: client.clientStatus ?? "",
        statusChange: {
          recallDate: null,
          comment: "",
        },
        portalAccess: client.portalAccess ?? { enabled: false, email: "" },
      });
      setTreatingClinician(client.treatingClinician?.current || null);
      setSupervisingClinician(client.supervisingClinician?.current || null);
      setOriginalData(client);
    }
  }, [client]);

  // Ensure clinicians are updated when client changes
  useEffect(() => {
    if (client) {
      setTreatingClinician(client.treatingClinician?.current);
      setSupervisingClinician(client.supervisingClinician?.current);
    }
  }, [client]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    logInfo("Client detail updated", {
      componentName: "ClientDetails",
      action: "updateField",
      field: name,
      value,
    });
  };

  const handleStatusChangeInputChange = (field, value) => {
    setFormData((prevData) => ({
      ...prevData,
      statusChange: {
        ...prevData.statusChange,
        [field]: value,
      },
    }));
    logInfo("Client status change updated", {
      componentName: "ClientDetails",
      action: "updateStatusChange",
      field,
      value,
    });
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      address: { ...prevData.address, [name]: value },
    }));
    logInfo("Client address updated", {
      componentName: "ClientDetails",
      action: "updateAddress",
      field: name,
      value,
    });
  };

  const handleDateChange = (date) => {
    setFormData((prevData) => ({ ...prevData, dateOfBirth: date }));
    logInfo("Client date of birth updated", {
      componentName: "ClientDetails",
      action: "updateDateOfBirth",
      date,
    });
  };

  const handleCancel = () => {
    setFormData({ ...originalData });
    setIsEditing(!isEditing);
    logInfo("Edit cancelled", {
      componentName: "ClientDetails",
      action: "cancelEdit",
    });
  };

  const handleAdditionalEmailChange = (index, field, value) => {
    const updatedEmails = formData.additionalEmails.map((emailObj, i) =>
      i === index ? { ...emailObj, [field]: value } : emailObj
    );
    setFormData((prevData) => ({
      ...prevData,
      additionalEmails: updatedEmails,
    }));
  };

  const handleAddAdditionalEmail = () => {
    setFormData((prevData) => ({
      ...prevData,
      additionalEmails: [
        ...prevData.additionalEmails,
        { label: "", email: "" },
      ],
    }));
  };

  const handleRemoveAdditionalEmail = (index) => {
    const updatedEmails = formData.additionalEmails.filter(
      (_, i) => i !== index
    );
    setFormData((prevData) => ({
      ...prevData,
      additionalEmails: updatedEmails,
    }));
  };

  const handleRemovePhone = (index) => {
    const updatedPhones = formData.phoneNumbers.filter((_, i) => i !== index);
    setFormData((prevData) => ({ ...prevData, phoneNumbers: updatedPhones }));
  };

  const handleSave = async (event) => {
    event.preventDefault();
    
    console.log("[Client Details] Save triggered - validating status change");
    
    // Validate status change requirements
    const validationError = validateStatusChange();
    if (validationError) {
      toast.error(validationError);
      return;
    }
    
    // Proceed with normal save (clinician updates are handled separately)
    await performSave();
  };

  const validateStatusChange = () => {
    // Only validate if status has changed from original
    if (formData.clientStatus !== originalData.clientStatus) {
      // Recall status requires a recall date
      if (formData.clientStatus === "recall" && !formData.statusChange.recallDate) {
        return "Recall date is required when changing status to Recall.";
      }
      
      // Other status requires a comment
      if (formData.clientStatus === "other" && !formData.statusChange.comment.trim()) {
        return "Comment is required when changing status to Other.";
      }
    }
    
    return null; // No validation errors
  };

  const performSave = async () => {
    setIsSaving(true);
    setSaveStatus("saving");

    const clientData = {
      clientId: formData.clientId,
      firstName: formData.firstName,
      lastName: formData.lastName,
      dateOfBirth: formData.dateOfBirth,
      type: client.type,
      address: {
        aptUnit: formData.address.aptUnit,
        streetNumber: formData.address.streetNumber,
        streetName: formData.address.streetName,
        city: formData.address.city,
        province: formData.address.province,
        postalCode: formData.address.postalCode,
      },
      email: formData.email,
      additionalEmails: formData.additionalEmails,
      healthCardNumber: formData.healthCardNumber,
      gender: formData.gender,
      pronouns: formData.pronouns,
      phoneNumbers: formData.phoneNumbers,
      clientStatus: formData.clientStatus,
      statusChange: formData.statusChange,
    };

    try {
      await dispatch(updateClient({ id: client._id, clientData })).unwrap();
      await dispatch(fetchClientById(client._id)).unwrap();
      setIsEditing(false);
      setSaveStatus("saved");
      setSuccessMessage(
        `Client details updated successfully for ${formData.firstName} ${formData.lastName}`
      );
      setShowSuccessModal(true);
      setTimeout(() => {
        setShowSuccessModal(false);
        setSaveStatus(null);
      }, 2000);
      logInfo("Client details saved", {
        componentName: "ClientDetails",
        action: "saveClientDetails",
        clientId: client._id,
      });
    } catch (error) {
      setSaveStatus("error");
      setTimeout(() => setSaveStatus(null), 3000);
      logError("Failed to save client details", {
        componentName: "ClientDetails",
        action: "saveClientDetails",
        error: error.message,
        clientId: client._id,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePhoneChange = (index, field, value) => {
    const updatedPhones = formData.phoneNumbers.map((phone, i) => {
      if (i === index) {
        if (field === "isMobile" && !value) {
          return { ...phone, [field]: value, smsConsent: false };
        }
        return { ...phone, [field]: value };
      }
      return phone;
    });
    setFormData((prevData) => ({ ...prevData, phoneNumbers: updatedPhones }));
    logInfo("Client phone number updated", {
      componentName: "ClientDetails",
      action: "updatePhoneNumber",
      index,
      field,
      value,
    });
  };

  const handleEmailChange = (index, e) => {
    const { value } = e.target;
    const updatedEmail = [...formData.email];
    updatedEmail[index] = value;
    setFormData((prevData) => ({ ...prevData, emails: updatedEmail }));
    logInfo("Client email updated", {
      componentName: "ClientDetails",
      action: "updateEmail",
      index,
      value,
    });
  };

  const handleAddPhone = () => {
    setFormData((prevData) => ({
      ...prevData,
      phoneNumbers: [...prevData.phoneNumbers, { type: "", number: "" }],
    }));
    logInfo("Phone number field added", {
      componentName: "ClientDetails",
      action: "addPhoneField",
    });
  };

  const handleEditClick = () => {
    setIsEditing(!isEditing);
    logInfo("Edit mode toggled", {
      componentName: "ClientDetails",
      action: "toggleEdit",
      isEditing: !isEditing,
    });
  };

  const handlePlaceSelected = (data) => {
    console.log(`[ClientDetails] Google Maps Data Object:`, data);
    
    let aptUnit = "";
    let streetNumber = "";
    let streetName = "";
    let city = "";
    let province = "";
    let postalCode = "";
    let country = "";
    let placeId = "";

    // Handle new API format with parsed data
    if (data.parsed) {
      console.log(`[ClientDetails] Using pre-parsed address data:`, data.parsed);
      aptUnit = data.parsed.aptUnit || "";
      streetNumber = data.parsed.streetNumber || "";
      streetName = data.parsed.streetName || "";
      city = data.parsed.city || "";
      province = data.parsed.province || "";
      postalCode = data.parsed.postalCode || "";
      country = data.parsed.country || "";
      placeId = data.parsed.placeId || "";
    } else {
      // Handle legacy format for backward compatibility
      const place = data;
      const addressComponents = place.address_components || place.addressComponents;
      
      const getComponent = (type) => {
        const component = addressComponents?.find((component) => 
          (component.types || []).includes(type)
        );
        return component?.long_name || component?.longText;
      };
      
      const getShortComponent = (type) => {
        const component = addressComponents?.find((component) => 
          (component.types || []).includes(type)
        );
        return component?.short_name || component?.shortText;
      };

      aptUnit = getComponent("subpremise") || "";
      streetNumber = getComponent("street_number") || "";
      streetName = getComponent("route") || "";
      city = getComponent("locality") || "";
      province = getShortComponent("administrative_area_level_1") || "";
      postalCode = getComponent("postal_code") || "";
      country = getComponent("country") || "";
      placeId = place.place_id || place.id || "";
    }

    setFormData((prevData) => ({
      ...prevData,
      address: {
        aptUnit: aptUnit || prevData.address.aptUnit,
        streetNumber: streetNumber || prevData.address.streetNumber,
        streetName: streetName || prevData.address.streetName,
        city: city || prevData.address.city,
        province: province || prevData.address.province,
        postalCode: postalCode || prevData.address.postalCode,
        country: country || prevData.address.country,
      },
    }));
    logInfo("Address autocompleted", {
      componentName: "ClientDetails",
      action: "autocompleteAddress",
      placeId: placeId,
    });
  };

  const handleEnablePortalAccess = async () => {
    if (!formData.email) {
      toast.error("Client must have an email address to enable portal access");
      return;
    }
    try {
      const response = await fetch(`/api/clients/${client._id}/portal-access`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: formData.email }),
      });
      if (!response.ok) throw new Error("Failed to enable portal access");
      const result = await response.json();
      toast.success(
        "Portal access enabled and credentials sent to client's email"
      );
      setFormData((prev) => ({
        ...prev,
        portalAccess: {
          ...prev.portalAccess,
          enabled: true,
          email: formData.email,
        },
      }));
      logInfo("Portal access enabled for client", {
        componentName: "ClientDetails",
        action: "enablePortalAccess",
        clientId: client._id,
      });
    } catch (error) {
      toast.error("Failed to enable portal access");
      logError("Failed to enable portal access", {
        componentName: "ClientDetails",
        action: "enablePortalAccess",
        error: error.message,
      });
    }
  };

  const renderPortalAccessButton = () => {
    if (!isEditing) return null;
    return (
      <button
        type="button"
        onClick={handleEnablePortalAccess}
        disabled={!formData.email}
        className={`inline-flex items-center px-4 py-2 text-sm font-semibold text-white rounded-sm shadow-sm ${
          formData.portalAccess?.enabled
            ? "bg-green-600 hover:bg-green-500"
            : "bg-blue-600 hover:bg-blue-500"
        }`}>
        {formData.portalAccess?.enabled
          ? "Reset Portal Access"
          : "Enable Portal Access"}
      </button>
    );
  };

  const getSaveButtonText = () => {
    if (saveStatus === "saving") return "Saving...";
    if (saveStatus === "saved") return "Saved!";
    if (saveStatus === "error") return "Error - Try Again";
    return "Save";
  };

  const getSaveButtonClass = () => {
    const baseClass =
      "inline-flex items-center rounded-sm text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background h-9 px-4 py-2 shadow-sm";

    if (saveStatus === "saving")
      return `${baseClass} bg-gray-400 text-white cursor-not-allowed`;
    if (saveStatus === "saved") return `${baseClass} bg-green-600 text-white`;
    if (saveStatus === "error")
      return `${baseClass} bg-red-600 text-white hover:bg-red-500`;
    return `${baseClass} bg-gray-900 text-white hover:bg-gray-800`;
  };

  return (
    <div className="bg-white rounded-sm shadow-sm border border-gray-200">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 tracking-tight">
          Client Details
        </h2>
        {!isEditing ? (
          <button
            type="button"
            onClick={handleEditClick}
            className="inline-flex items-center rounded-sm text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background bg-gray-900 text-white hover:bg-gray-800 h-9 px-4 py-2 shadow-sm">
            Edit
          </button>
        ) : (
          <div className="flex space-x-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className={getSaveButtonClass()}>
              {getSaveButtonText()}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              disabled={isSaving}
              className="inline-flex items-center rounded-sm text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background bg-gray-100 text-gray-900 hover:bg-gray-200 h-9 px-4 py-2 shadow-sm">
              Cancel
            </button>
            {renderPortalAccessButton()}
          </div>
        )}
      </div>

      {/* Form Content */}
      <div className="p-4 space-y-4">
        {/* Full Name & Primary Email */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">
              First Name
            </label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              readOnly={!isEditing}
              className="flex h-9 w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">
              Last Name
            </label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              readOnly={!isEditing}
              className="flex h-9 w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">
              Primary Email
            </label>
            <input
              type="text"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              readOnly={!isEditing}
              placeholder={isEditing ? "New Email" : ""}
              className="flex h-9 w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
        </div>

        {/* Client ID & Clinicians */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">
              Client ID
            </label>
            <input
              type="text"
              name="clientId"
              value={formData.clientId}
              onChange={handleInputChange}
              readOnly={!isEditing}
              className="flex h-9 w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
          <div className="space-y-1.5">
            <div className="text-sm font-medium text-gray-700 mb-1">
              Treating Clinician
            </div>
            {isEditing ? (
              <div className="flex items-center space-x-2">
                <div className="flex-1 p-2 border border-gray-300 rounded-sm bg-gray-50">
                  <span className="text-sm text-gray-700">
                    {treatingClinician 
                      ? `${treatingClinician.firstName} ${treatingClinician.lastName}`
                      : "No treating clinician assigned"
                    }
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setClinicianModalType("treating");
                    setShowClinicianModal(true);
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-sm transition-colors">
                  <PencilIcon className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <input
                type="text"
                name="treatingClinician"
                value={
                  selectedTreatingClinician
                    ? `${selectedTreatingClinician.firstName} ${selectedTreatingClinician.lastName}`
                    : "No treating clinician assigned"
                }
                readOnly
                className="flex h-9 w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              />
            )}
          </div>
          <div className="space-y-1.5">
            <div className="text-sm font-medium text-gray-700 mb-1">
              Supervising Clinician
            </div>
            {isEditing ? (
              <div className="flex items-center space-x-2">
                <div className="flex-1 p-2 border border-gray-300 rounded-sm bg-gray-50">
                  <span className="text-sm text-gray-700">
                    {supervisingClinician 
                      ? `${supervisingClinician.firstName} ${supervisingClinician.lastName}`
                      : "No supervising clinician assigned"
                    }
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setClinicianModalType("supervising");
                    setShowClinicianModal(true);
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-sm transition-colors">
                  <PencilIcon className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <input
                type="text"
                name="supervisingClinician"
                value={
                  selectedSupervisingClinician
                    ? `${selectedSupervisingClinician.firstName} ${selectedSupervisingClinician.lastName}`
                    : "No supervising clinician assigned"
                }
                readOnly
                className="flex h-9 w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              />
            )}
          </div>
        </div>

        {/* DOB, Health Card, QBO Client ID */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">
              Health Card #
            </label>
            <input
              type="text"
              name="healthCardNumber"
              value={formData.healthCardNumber}
              onChange={handleInputChange}
              readOnly={!isEditing}
              className="flex h-9 w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">
              DOB (dd/mm/yyyy)
            </label>
            <DatePicker
              className="flex h-9 w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              selected={formData.dateOfBirth}
              onChange={handleDateChange}
              readOnly={!isEditing}
              disabled={!isEditing}
              dateFormat="dd/MM/yyyy"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">
              QBO Client ID
            </label>
            <input
              type="text"
              name="QBOClientId"
              value={formData.QBOClientId}
              onChange={handleInputChange}
              readOnly
              className="flex h-9 w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
        </div>

        {/* Gender, Pronouns, Client Status */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">Gender</label>
            <GenderSelector
              value={formData.gender}
              onChange={(e) => {
                if (isEditing) {
                  setFormData((prevData) => ({
                    ...prevData,
                    gender: e.target.value,
                  }));
                  logInfo("Gender updated", {
                    componentName: "ClientDetails",
                    action: "updateGender",
                    gender: e.target.value,
                  });
                }
              }}
              disabled={!isEditing}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">
              Pronouns
            </label>
            <PronounSelector
              value={formData.pronouns}
              onChange={(e) => {
                if (isEditing) {
                  setFormData((prevData) => ({
                    ...prevData,
                    pronouns: e.target.value,
                  }));
                  logInfo("Pronouns updated", {
                    componentName: "ClientDetails",
                    action: "updatePronouns",
                    pronouns: e.target.value,
                  });
                }
              }}
              disabled={!isEditing}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">
              Client Status
            </label>
            {isEditing ? (
              <select
                value={formData.clientStatus}
                onChange={(e) => {
                  setFormData((prevData) => ({
                    ...prevData,
                    clientStatus: e.target.value,
                    statusChange: {
                      recallDate: null,
                      comment: "",
                    },
                  }));
                  logInfo("Client status updated", {
                    componentName: "ClientDetails",
                    action: "updateClientStatus",
                    clientStatus: e.target.value,
                  });
                }}
                className="flex h-9 w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="discharged">Discharged</option>
                <option value="recall">Recall</option>
                <option value="deceased">Deceased</option>
                <option value="other">Other</option>
              </select>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setShowStatusHistoryModal(true);
                  logInfo("Status history modal opened", {
                    componentName: "ClientDetails",
                    action: "openStatusHistory",
                    clientId: client._id,
                  });
                }}
                className={`flex h-9 w-full rounded-sm border px-3 py-2 text-sm font-medium capitalize hover:shadow-sm transition-all cursor-pointer ${
                  formData.clientStatus === 'active' ? 'border-green-300 bg-green-50 text-green-800 hover:bg-green-100' :
                  formData.clientStatus === 'inactive' ? 'border-gray-300 bg-gray-50 text-gray-800 hover:bg-gray-100' :
                  formData.clientStatus === 'discharged' ? 'border-blue-300 bg-blue-50 text-blue-800 hover:bg-blue-100' :
                  formData.clientStatus === 'recall' ? 'border-orange-300 bg-orange-50 text-orange-800 hover:bg-orange-100' :
                  formData.clientStatus === 'deceased' ? 'border-red-300 bg-red-50 text-red-800 hover:bg-red-100' :
                  formData.clientStatus === 'other' ? 'border-purple-300 bg-purple-50 text-purple-800 hover:bg-purple-100' :
                  'border-gray-300 bg-gray-50 text-gray-500 hover:bg-gray-100'
                }`}
                title="Click to view status change history">
                {formData.clientStatus || 'No status set'}
                <span className="ml-auto text-xs opacity-70">
                  {client?.statusHistory?.length > 0 && `(${client.statusHistory.length})`}
                </span>
              </button>
            )}
          </div>
        </div>

        {/* Status Change Fields - Conditional */}
        {isEditing && formData.clientStatus && (
          <div className="space-y-3 bg-blue-50 p-4 rounded-sm border border-blue-200">
            <h4 className="text-sm font-semibold text-blue-900">
              Status Change Details
            </h4>
            
            {/* Recall Date - Only for recall status */}
            {formData.clientStatus === "recall" && (
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">
                  Recall Date <span className="text-red-500">*</span>
                </label>
                <DatePicker
                  className="flex h-9 w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  selected={formData.statusChange.recallDate}
                  onChange={(date) => handleStatusChangeInputChange("recallDate", date)}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Select recall date"
                  minDate={new Date()}
                />
              </div>
            )}

            {/* Comment Field */}
            {(formData.clientStatus === "recall" || 
              formData.clientStatus === "discharged" || 
              formData.clientStatus === "other") && (
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">
                  Comment 
                  {formData.clientStatus === "other" && (
                    <span className="text-red-500">*</span>
                  )}
                  {formData.clientStatus !== "other" && (
                    <span className="text-gray-500 text-xs">(optional)</span>
                  )}
                </label>
                <textarea
                  value={formData.statusChange.comment}
                  onChange={(e) => handleStatusChangeInputChange("comment", e.target.value)}
                  placeholder={
                    formData.clientStatus === "recall" 
                      ? "Optional note about recall reason..."
                      : formData.clientStatus === "discharged"
                      ? "Optional note about discharge..."
                      : "Required explanation for 'Other' status..."
                  }
                  className="flex w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 min-h-[80px] resize-none"
                />
              </div>
            )}
          </div>
        )}

        {/* Address */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-gray-700">Address</label>
          {isEditing && (
            <div className="mb-3">
              <GoogleMapsAutocomplete
                onPlaceSelected={handlePlaceSelected}
                inputId="autocomplete-address"
              />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <input
                type="text"
                name="aptUnit"
                value={formData.address.aptUnit}
                onChange={handleAddressChange}
                readOnly={!isEditing}
                placeholder="Apt/Unit"
                className="flex h-9 w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
            <div className="space-y-1.5">
              <input
                type="text"
                name="streetNumber"
                value={formData.address.streetNumber}
                onChange={handleAddressChange}
                readOnly={!isEditing}
                placeholder="Street Number"
                className="flex h-9 w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
            <div className="space-y-1.5">
              <input
                type="text"
                name="streetName"
                value={formData.address.streetName}
                onChange={handleAddressChange}
                readOnly={!isEditing}
                placeholder="Street Name"
                className="flex h-9 w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
            <div className="space-y-1.5">
              <input
                type="text"
                name="city"
                value={formData.address.city}
                onChange={handleAddressChange}
                readOnly={!isEditing}
                placeholder="City"
                className="flex h-9 w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
            <div className="space-y-1.5">
              <input
                type="text"
                name="province"
                value={formData.address.province}
                onChange={handleAddressChange}
                readOnly={!isEditing}
                placeholder="Province"
                className="flex h-9 w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
            <div className="space-y-1.5">
              <input
                type="text"
                name="postalCode"
                value={formData.address.postalCode}
                onChange={handleAddressChange}
                readOnly={!isEditing}
                placeholder="Postal Code"
                className="flex h-9 w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
            <div className="space-y-1.5">
              <input
                type="text"
                name="country"
                value={formData.address.country}
                onChange={handleAddressChange}
                readOnly={!isEditing}
                placeholder="Country"
                className="flex h-9 w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
          </div>
        </div>

        {/* Additional Emails */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-gray-700">
            Additional Emails
          </label>
          {formData.additionalEmails.length > 0 ? (
            formData.additionalEmails.map((email, index) => (
              <div key={index} className="grid grid-cols-2 gap-3 items-center">
                <input
                  type="text"
                  name="type"
                  value={email.label}
                  onChange={(e) =>
                    handleAdditionalEmailChange(index, "type", e.target.value)
                  }
                  readOnly={!isEditing}
                  placeholder="Email Type"
                  className="flex h-9 w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
                <div className="flex items-center space-x-2">
                  <input
                    type="email"
                    name="email"
                    value={email.email}
                    onChange={(e) =>
                      handleAdditionalEmailChange(
                        index,
                        "email",
                        e.target.value
                      )
                    }
                    readOnly={!isEditing}
                    placeholder="Email Address"
                    className="flex h-9 w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                  {isEditing && (
                    <CircularButton
                      onClick={() => handleRemoveAdditionalEmail(index)}
                      icon={<TrashIcon className="h-5 w-5" />}
                      label="Remove"
                      className="m-2"
                    />
                  )}
                </div>
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-500">No additional emails added.</p>
          )}
          {isEditing && (
            <CircularButton
              onClick={handleAddAdditionalEmail}
              icon={<PlusIcon className="h-5 w-5" />}
              label="Add Email"
              className="mt-2 m-2"
            />
          )}
        </div>

        {/* Phone Numbers */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-gray-700">
            Phone Numbers
          </label>
          {formData.phoneNumbers.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {formData.phoneNumbers.map((phone, index) => (
                <div
                  key={index}
                  className="bg-gray-50/50 p-3 rounded-sm border border-gray-200">
                  <div className="space-y-3">
                    <select
                      name="type"
                      value={phone.type}
                      onChange={(e) =>
                        isEditing &&
                        handlePhoneChange(index, "type", e.target.value)
                      }
                      disabled={!isEditing}
                      className="flex h-9 w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50">
                      <option value="">Select Type</option>
                      <option value="Home">Home</option>
                      <option value="Work">Work</option>
                      <option value="Mobile">Mobile</option>
                      <option value="Fax">Fax</option>
                      <option value="Other">Other</option>
                    </select>
                    <input
                      type="text"
                      name="number"
                      value={phone.number}
                      onChange={(e) =>
                        isEditing &&
                        handlePhoneChange(index, "number", e.target.value)
                      }
                      readOnly={!isEditing}
                      placeholder="Phone Number"
                      className="flex h-9 w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    />
                    <textarea
                      name="note"
                      value={phone.note || ""}
                      onChange={(e) =>
                        isEditing &&
                        handlePhoneChange(index, "note", e.target.value)
                      }
                      readOnly={!isEditing}
                      placeholder="Note (e.g. Best time to call, extension, Dad, etc.)"
                      className="flex w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 min-h-[60px] resize-none"></textarea>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          name="isMobile"
                          checked={phone.isMobile}
                          onChange={(e) =>
                            isEditing &&
                            handlePhoneChange(
                              index,
                              "isMobile",
                              e.target.checked
                            )
                          }
                          disabled={!isEditing}
                          className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                        />
                        <label className="text-sm font-medium text-gray-700">
                          Mobile
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          name="smsConsent"
                          checked={phone.smsConsent}
                          disabled={!isEditing || !phone.isMobile}
                          onChange={() =>
                            isEditing &&
                            handlePhoneChange(
                              index,
                              "smsConsent",
                              !phone.smsConsent
                            )
                          }
                          className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                        />
                        <label className="text-sm font-medium text-gray-700">
                          Receive SMS Appointment Reminders
                        </label>
                      </div>
                    </div>
                    {isEditing && (
                      <div className="flex justify-end">
                        <CircularButton
                          onClick={() => handleRemovePhone(index)}
                          icon={<TrashIcon className="h-5 w-5" />}
                          label="Remove"
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No phone numbers added.</p>
          )}
          {isEditing && (
            <CircularButton
              onClick={handleAddPhone}
              icon={<PlusIcon className="h-5 w-5" />}
              label="Add Phone"
              className="mt-2 m-2"
            />
          )}
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-sm shadow-xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-6 w-6 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">
                  {successMessage}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Clinician Update Modal */}
      {console.log("Modal state:", { showClinicianModal, clinicianModalType })}
      <ModifyClinicianModal
        isOpen={showClinicianModal}
        onClose={() => {
          console.log("Modal closing");
          setShowClinicianModal(false);
          setClinicianModalType(null);
        }}
        client={client}
        clinicianType={clinicianModalType}
        context="client"
        onSuccess={() => {
          // Refresh client data after successful update
          if (client?._id) {
            dispatch(fetchClientById(client._id));
          }
        }}
      />

      {/* Status History Modal */}
      <StatusHistoryModal
        isOpen={showStatusHistoryModal}
        onClose={() => setShowStatusHistoryModal(false)}
        client={client}
      />
    </div>
  );
}
